<template>
  <Teleport to="body">
  <TransitionRoot as="template" :show="modelValue">
    <Dialog as="div" class="relative z-[100]" @close="handleDialogClose">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:scale-95"
          >
            <DialogPanel :class="panelClass">
              <form @submit.prevent="$emit('submit')" class="flex flex-col max-h-[80vh] w-full">
                <!-- header -->
                <div class="px-6 py-4 border-b bg-white flex items-start justify-between">
                  <div class="min-w-0 flex-1">
                    <slot name="header">
                      <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-gray-900">{{ title }}</DialogTitle>
                      <div v-if="subtitle" class="mt-1 text-sm text-gray-500">{{ subtitle }}</div>
                    </slot>
                  </div>

                  <div v-if="showClose" class="ml-4 shrink-0">
                    <button type="button" class="text-gray-400 hover:text-gray-600" @click="close" aria-label="Close">
                      ✕
                    </button>
                  </div>
                </div>

                <!-- body (scrollable) -->
                <div class="p-6 overflow-y-auto" style="flex:1 1 auto">
                  <slot />
                </div>

                <!-- footer (fixed) -->
                <div class="px-6 py-4 border-t bg-white flex justify-end gap-3">
                  <!-- Focus fallback: hidden focusable element so FocusTrap always finds at least one focusable control -->
                  <button type="button" class="sr-only" tabindex="0" aria-hidden="true">.</button>
                  <slot name="footer" />
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';

interface Props {
  modelValue: boolean;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showClose?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'submit'): void;
}>();

function close(event?: Event) {
  event?.stopPropagation?.();
  emit('update:modelValue', false);
}

function handleDialogClose(value: boolean) {
  if (value === false) {
    close();
  }
}

const panelClass = computed(() => {
  const base = 'relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full';
  switch (props.size) {
    case 'sm':
      return `${base} sm:max-w-md`;
    case 'lg':
      return `${base} sm:max-w-4xl`;
    case 'xl':
      return `${base} sm:max-w-6xl`;
    default:
      return `${base} sm:max-w-2xl`;
  }
});
</script>
